import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import MobileLayout from '../../components/Mobile/MobileLayout';
import { GET_CATEGORIES } from '../../graphql/queries';

library.add(fas);

const parseIconClass = (iconClass: string) => {
  if (!iconClass) return null;
  const parts = iconClass.trim().split(' ');
  if (parts.length >= 2) {
    const prefix = parts[0];
    const iconName = parts[1].replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    return { prefix, iconName };
  }
  return null;
};

const MobileCategories: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_CATEGORIES);
  const categories = data?.rootCategories || [];

  return (
    <MobileLayout>
      {/* Header */}
      <div
        className="px-4 py-4"
        style={{
          background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
          color: 'white',
        }}
      >
        <h1 className="fw-bold mb-1" style={{ fontSize: 24 }}>Browse Categories</h1>
        <p style={{ fontSize: 14, opacity: 0.9, margin: 0 }}>Find what you&apos;re looking for</p>
      </div>

      {/* Categories List */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '3px solid var(--pw-border)',
              borderTopColor: 'var(--pw-primary)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : error ? (
        <div className="text-center py-5 px-4">
          <div
            className="mx-auto mb-3 d-flex align-items-center justify-content-center"
            style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(214, 86, 69, 0.1)' }}
          >
            <svg style={{ width: 32, height: 32, color: 'var(--pw-danger)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="fw-bold mb-2" style={{ fontSize: 18, color: 'var(--pw-text)' }}>Error loading categories</h2>
          <p style={{ fontSize: 14, color: 'var(--pw-text-muted)' }}>Please try again later.</p>
        </div>
      ) : (
        <div className="p-3">
          <div
            style={{
              background: 'var(--pw-surface)',
              border: '1px solid var(--pw-border)',
              borderRadius: 0,
              overflow: 'hidden',
              boxShadow: '0 4px 14px rgba(116, 67, 35, 0.05)',
            }}
          >
            {categories.map((category: any, index: number) => {
              const parsedIcon = parseIconClass(category.icon);
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => navigate(`/category/${category.slug}`)}
                  className="d-flex align-items-center gap-3 cursor-pointer"
                  style={{
                    padding: 16,
                    borderBottom: index < categories.length - 1 ? '1px solid var(--pw-border-soft)' : 'none',
                    background: 'var(--pw-surface)',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--pw-bg-soft)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--pw-surface)')}
                >
                  {/* Icon */}
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 14,
                      background: 'rgba(232, 93, 76, 0.1)',
                      color: 'var(--pw-primary)',
                    }}
                  >
                    {parsedIcon ? (
                      <FontAwesomeIcon icon={parsedIcon as any} style={{ fontSize: 18 }} />
                    ) : (
                      <FontAwesomeIcon icon={faEllipsisH} style={{ fontSize: 18 }} />
                    )}
                  </div>

                  {/* Category Name */}
                  <div className="flex-grow-1">
                    <h3 className="fw-semibold mb-0" style={{ fontSize: 15, color: 'var(--pw-text)' }}>
                      {category.name}
                    </h3>
                    {category.childrenList && category.childrenList.length > 0 && (
                      <p className="mb-0" style={{ fontSize: 12, color: 'var(--pw-text-muted)', marginTop: 2 }}>
                        {category.childrenList.length} subcategories
                      </p>
                    )}
                  </div>

                  {/* Arrow */}
                  <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 14, color: 'var(--pw-text-muted)' }} />
                </motion.div>
              );
            })}
          </div>

          {/* Empty State */}
          {categories.length === 0 && (
            <div className="text-center py-5">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--pw-bg-soft)' }}
              >
                <FontAwesomeIcon icon={faEllipsisH} style={{ fontSize: 32, color: 'var(--pw-text-muted)' }} />
              </div>
              <p style={{ fontSize: 14, color: 'var(--pw-text-muted)' }}>No categories available</p>
            </div>
          )}
        </div>
      )}
    </MobileLayout>
  );
};

export default MobileCategories;
